import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { formatCurrency } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { Observable, take } from 'rxjs';

import { TrackError } from '@fantaz/core/utils';
import {
  actionTypes,
  CasualTournamentDetails,
  CasualTournamentEntity,
  columnTypes,
  CurrentUser,
  Game,
  IColumnModel,
  IPageModel,
  ITableConfig,
  NextEvent,
  TournamentTagsEnum,
  TournamentType,
  UserPasses,
} from '@fantaz/shared/data-access/models';
import { UIStore } from '@fantaz/shared/data-access/store';

function getPrizeImage(tournament: CasualTournamentEntity): string | null {
  const prizeImageCard = tournament.prizeDetails?.prizeImageCard;

  if (!prizeImageCard) {
    return null;
  }

  if (typeof prizeImageCard !== 'string') {
    return prizeImageCard.url ?? '';
  }

  return prizeImageCard;
}

const toPlayNowColumn = {
  id: 'toPlayNow',
  title: 'To Play Now',
  type: () => columnTypes.CUSTOM,
  mapping: (element: any) => '',
  tooltip: () => null,
  customElement: (
    element: CasualTournamentEntity & { toPlayNow?: NextEvent },
  ) => {
    if (!element?.toPlayNow) {
      return '<div class="loader"></span>';
    }

    const seats = element?.toPlayNow?.seats;
    const used = element?.toPlayNow?.participants?.length;
    const available = seats - used;

    if (element?.lifeExperience) {
      if (!used) {
        return `
          <div>
            <div class="mat-body-large">Just started. Be the first!</div>
          </div>
        `;
      }

      return `
        <div>
          <div class="mat-body-large">${used} players participating</div>
          <div class="mat-body-small">1st place <span class="font-color--primary">${element?.toPlayNow?.participants[0]?.player}<span></div>
        </div>
      `;
    }

    if (used > 0) {
      if (seats > 5001) {
        return `
          <div>
            <div class="mat-body-large">Unlimited seats</div>
            <div class="mat-body-small">1st place <span class="font-color--primary">${element?.toPlayNow?.participants[0]?.player}<span></div>
          </div>
        `;
      }

      return `
        <div>
          <div class="mat-body-large">${available} available seat(${used}/${seats})</div>
          <div class="mat-body-small">1st place <span class="font-color--primary">${element?.toPlayNow?.participants[0]?.player}<span></div>
        </div>
      `;
    }

    return `
      <div>
        <div class="mat-body-large">All seats available</div>
        <div class="mat-body-small">Play to be the first place!</div>
      </div>
    `;
  },
};

@Component({
  selector: 'fz-tournaments-table-view',
  templateUrl: './tournaments-table-view.component.html',
  styleUrls: ['./tournaments-table-view.component.scss'],
})
export class TournamentsTableViewComponent implements OnInit, OnChanges {
  #destroyRef = inject(DestroyRef);
  #breakpointObserver = inject(BreakpointObserver);

  dataSource!: CasualTournamentEntity[];
  details$: Observable<CasualTournamentDetails | null> = new Observable();
  details: CasualTournamentDetails | null = null;
  displayedColumns: IColumnModel[];
  filteredTournaments: CasualTournamentEntity[] = [];

  page: IPageModel = {
    pageIndex: 0,
    pageSize: 20,
  };

  @Input()
  tournaments: Array<CasualTournamentEntity & { toPlayNow?: NextEvent }>;

  @Input()
  loading: boolean | null = false;

  @Input()
  isLoggedIn: boolean;

  @Input()
  isEvent: boolean;

  @Input()
  isGameDetailsView = false;

  @Input()
  showCategory = false;

  @Input()
  passesLoaded = false;

  @Input()
  includeToPlayNow = false;

  @Input()
  showCashPlayButton = false;

  _tournamentType?: TournamentType | null;

  get tournamentType(): TournamentType | undefined | null {
    return this._tournamentType;
  }

  @Input()
  set tournamentType(
    data: TournamentType | [TournamentType, boolean] | undefined | null,
  ) {
    let value: TournamentType;
    let showToPlayNow = false;
    if (Array.isArray(data)) {
      value = data[0];
      showToPlayNow = data[1] ?? false;
    } else {
      value = data as TournamentType;
    }

    let actions: actionTypes[];
    if (value === TournamentType.CASH) {
      actions = [
        ...(this.isGameDetailsView ? [actionTypes.PLAY] : []),
        actionTypes.SHARE,
        actionTypes.DETAILS,
      ];
      if (this.showCashPlayButton) {
        actions.push(actionTypes.PLAY);
      }
      this.configuration.fixedLayout = true;
      this.displayedColumns = [
        {
          id: 'game',
          title: 'Game',
          type: () => columnTypes.AVATAR,
          mapping: (element: any) => {
            return {
              imageUrl: this.getGameImage(element.game),
              name: element.game?.name,
            };
          },
          tooltip: () => null,
        },
        {
          id: 'name',
          title: 'Tournament',
          type: () => columnTypes.CUSTOM,
          mapping: (element: any) => element.name,
          customElement: (
            element: CasualTournamentEntity & { toPlayNow?: NextEvent } & any,
          ) => {
            if (!showToPlayNow || element.lifeExperience) {
              return element.name;
            }

            if (isTournamentEnded(element)) {
              return element.name;
            }

            if (!element?.toPlayNow) {
              return '<div class="loader"></span>';
            }

            return `
              <div>
                <div class="mat-body-large fw--500">${
                  element?.toPlayNow?.eventCode ?? ''
                }</div>
                <div class="mat-body-small">${
                  element?.toPlayNow?.eventName
                }</div>
              </div>
            `;
          },
          tooltip: () => null,
        },
        ...(this.isGameDetailsView
          ? [
              {
                id: 'seats',
                title: 'Seats',
                type: () => columnTypes.DEFAULT,
                mapping: (element: any) => {
                  return element.room?.seatsPerInstance || 0;
                },
                tooltip: () => null,
              },
            ]
          : []),

        {
          id: 'prizeRange',
          title: `${this.isEvent ? '1st Prize' : 'Prize Range'}`,
          type: () => (this.isEvent ? columnTypes.CURRENCY : columnTypes.RANGE),
          mapping: (element: any) => {
            const range = {
              start: element.prizeRange.min,
              end: element.prizeRange.max,
            };
            if (this.isEvent) {
              const prize = element.room.prizeDetails.find(
                (p: any) => p.rank === 1,
              );

              if (prize) {
                return prize.prizeValue;
              }

              TrackError.trackCustomError(
                'cash-tournament-no-prize-value',
                `No prize value found for the cash tournament: ${element.uuid}`,
              );
            }
            return range;
          },
          tooltip: () => null,
        },
        {
          id: 'entryFeeRange',
          title: `${this.isEvent ? 'Entry Fee' : 'Entry Fee Range'}`,
          type: () => (this.isEvent ? columnTypes.CURRENCY : columnTypes.RANGE),
          mapping: (element: any) => {
            const range = {
              start: element.entryFeeRange.start,
              end: element.entryFeeRange.end,
            };
            return this.isEvent ? element.room?.entryAmount || 0 : range;
          },
          tooltip: () => null,
        },
        ...(showToPlayNow ? [toPlayNowColumn] : []),
      ];
    } else if (value === TournamentType.PRIZE) {
      actions = [actionTypes.PLAY, actionTypes.SHARE, actionTypes.DETAILS];
      this.displayedColumns = [
        {
          id: 'game',
          title: 'Game',
          type: () => columnTypes.AVATAR,
          mapping: (element: any) => {
            return {
              imageUrl: this.getGameImage(element.game),
              name: element.game?.name,
            };
          },
          tooltip: () => null,
        },
        {
          id: 'name',
          title: 'Tournament',
          type: () => columnTypes.CUSTOM,
          mapping: (element: any) => element.name,
          customElement: (
            element: CasualTournamentEntity & { toPlayNow?: NextEvent },
          ) => {
            if (!showToPlayNow || element.lifeExperience) {
              return `
                <div class="mat-body-large">${element.name}</div>
              `;
            }

            if (!element?.toPlayNow) {
              return '<div class="loader"></span>';
            }

            return `
            <div>
              <div class="mat-body-large  fw--500">${
                element?.toPlayNow?.eventCode ?? ''
              }</div>
              <div class="mat-body-small">${element?.toPlayNow?.eventName}</div>
            </div>
            `;
          },
          tooltip: () => null,
        },
        {
          id: 'prize',
          title: 'Prize',
          type: () => columnTypes.CUSTOM,
          customElement: (element: any) => {
            const prizeImg =
              getPrizeImage(element) || '/shared/svg/trophy-prize-icon.svg';
            return `<div class="custom-prize">
            <img class="custom-prize__img" src="${prizeImg}" />
            <span class="custom-prize__text">${element?.prizeDetails?.prizeName}</span>
          </div>`;
          },
          mapping: () => null,
          tooltip: () => null,
        },
        {
          id: 'entryFee',
          title: 'Entry Fee',
          type: () => columnTypes.CURRENCY,
          mapping: (element: any) => element.eventSchedules[0].entryAmount,
          tooltip: () => null,
        },
        ...(showToPlayNow ? [toPlayNowColumn] : []),
      ];
    } else {
      actions = [actionTypes.SHARE, actionTypes.DETAILS];
      this.displayedColumns = [
        {
          id: 'game',
          title: 'Game',
          type: () => columnTypes.AVATAR,
          mapping: (element: any) => {
            return {
              imageUrl: this.getGameImage(element.game),
              name: element.game?.name,
            };
          },
          tooltip: () => null,
        },
        {
          id: 'name',
          title: 'Tournament Name',
          type: () => columnTypes.DEFAULT,
          mapping: (element: any) => element.name,
          tooltip: () => null,
        },
        {
          id: 'prize',
          title: 'Prize',
          type: (element: any) =>
            element.lifeExperience ? columnTypes.CUSTOM : columnTypes.RANGE,
          mapping: (element: any) => {
            return {
              start: element.prizeRange.min,
              end: element.prizeRange.max,
            };
          },
          customElement: (element: any) => {
            const prizeImg = element.lifeExperience
              ? element.prizeDetails.prizeImage ||
                '/shared/svg/trophy-prize-icon.svg'
              : '/shared/svg/cash-prize-icon.svg';
            const prizeLabel = element.lifeExperience
              ? element.prizeDetails.prizeName
              : `$${this.formatAmount(element.prizeDetails.prizeValue)} USD`;
            return `<div class="custom-prize">
                <img class="custom-prize__img" src="${prizeImg}" />
                <span class="custom-prize__text">${prizeLabel}</span>
              </div>`;
          },
          tooltip: () => null,
        },
        {
          id: 'entryFee',
          title: 'Entry Fee',
          type: () => columnTypes.CURRENCY,
          mapping: (element: any) => element.eventSchedules[0].entryAmount,
          tooltip: () => null,
        },

        ...(showToPlayNow ? [toPlayNowColumn] : []),
      ];
    }

    if (this.showCategory) {
      this.displayedColumns.splice(2, 0, {
        id: 'category',
        title: 'Category',
        type: () => columnTypes.CUSTOM,
        mapping: (element: any) => element.name,
        customElement: (element: any) => {
          const tag = this.filterTagsIcon(element.metadata?.tags || []);
          const parseTag = tag ? tag.toLowerCase().replace(' ', '_') : '';
          return `<div class='category-img' style="display:flex; align-items:center">
            <img src='/shared/img/tournaments/tournament_types/${parseTag}.svg' />
            <span class='mat-body-1 no-margin' style="margin-left:8px">${tag}<span>
            </div>`;
        },

        tooltip: () => null,
      });
    }

    if (this.isGameDetailsView) {
      this.displayedColumns.shift();
    }

    this.displayedColumns.push({
      id: 'actions',
      title: 'Actions',
      type: () => columnTypes.ACTIONS,
      mapping: () => null,
      tooltip: () => null,
      tournament: (element: any) => element,
      eventSchedule: (element: any) => element.eventSchedules[0],
      passUuid: (element: any) => this.passValue(element),
      playButtonText: (element: any) => this.getPlayButtonText(element),
      playButtonIsBasic: false,
      actionsToShow: () => actions,
    });

    this._tournamentType = value;
  }

  @Input()
  currentUser?: CurrentUser | null | undefined;

  @Input()
  location?: boolean;
  @Input()
  locationLoaded?: boolean;

  @Input()
  tournamentPassesForUser: UserPasses[];

  @Input()
  total = 0;

  @Output()
  tournamentPressed = new EventEmitter<CasualTournamentEntity>();

  @Output()
  share = new EventEmitter<CasualTournamentEntity>();

  @Output()
  loadMoreTournaments = new EventEmitter<IPageModel>();

  configuration: ITableConfig = {
    tableName: 'tournament-list',
    pagination: {
      paginationTotal: this.total,
      sizeQueryParam: 'pageSize',
      indexQueryParam: 'pageIndex',
      pageSize: 20,
      showPagination: true,
      paginationSizeOptions: [5, 10, 20],
    },
  };

  mobileMatches = [Breakpoints.XSmall, Breakpoints.Small];
  isMobile = signal<boolean>(
    this.#breakpointObserver.isMatched(this.mobileMatches),
  );

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private route: ActivatedRoute,
    private readonly uiStore: UIStore,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
  ) {
    Object.keys(TournamentTagsEnum).forEach((tag) => {
      const _tag = tag.toLowerCase();
      this.matIconRegistry.addSvgIcon(
        `${_tag}`,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          `/shared/img/tournaments/tournament_types/flat/${_tag}.svg`,
        ),
      );
    });

    this.#breakpointObserver
      .observe(this.mobileMatches)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        this.isMobile.set(result.matches);
      });
  }

  ngOnInit() {
    if (this.configuration?.pagination) {
      this.configuration.pagination.indexQueryParam = `${this.tournamentType?.toLocaleLowerCase()}PageIndex`;
      this.configuration.pagination.sizeQueryParam = `${this.tournamentType?.toLocaleLowerCase()}PageSize`;
      this.configuration.pagination.pageSize = 20;
    }
    this.uiStore.userPreferences$.pipe(take(1)).subscribe((preferences) => {
      const queryParams = this.route.snapshot.root.queryParams;

      if (preferences.pagination.pageSize) {
        this.page.pageSize = preferences.pagination.pageSize;
      }

      if (
        queryParams[
          this.configuration?.pagination?.indexQueryParam || 'pageIndex'
        ]
      ) {
        this.page.pageIndex =
          queryParams[
            this.configuration?.pagination?.indexQueryParam || 'pageIndex'
          ];
      }

      if (
        queryParams[
          this.configuration?.pagination?.sizeQueryParam || 'pageSize'
        ]
      ) {
        this.page.pageSize =
          queryParams[
            this.configuration?.pagination?.sizeQueryParam || 'pageSize'
          ];
      }
    });

    this.filterTournaments();
  }

  filterTagsIcon(tags: string[]) {
    const genericTournaments: string[] = [
      TournamentTagsEnum.CHARITY_EVENT,
      TournamentTagsEnum.CELEBRITY_EVENT,
      TournamentTagsEnum.ZBO_TOURNAMENT,
      TournamentTagsEnum.FREE_TOURNAMENT,
    ];

    const generics = tags.filter(
      (tournament) => genericTournaments.indexOf(tournament.toUpperCase()) >= 0,
    );
    const prize = tags.filter(
      (tournament) =>
        [TournamentTagsEnum.PRIZE_TOURNAMENT as string].indexOf(
          tournament.toUpperCase(),
        ) >= 0,
    );

    if (generics.length) {
      return generics[0]?.replace('Zbo', 'ZBO');
    } else if (prize.length) {
      return this.capitalize(TournamentTagsEnum.PRIZE_TOURNAMENT);
    }

    return this.capitalize(TournamentTagsEnum.CASH_TOURNAMENT);
  }

  capitalize(value: string) {
    return value
      .toLowerCase()
      .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
  }

  getGameImage(
    game: Game,
    type: 'avatar' | 'card' | 'background' = 'avatar',
  ): string {
    const result = game.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    return `/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  onTournamentPress(tournament: CasualTournamentEntity) {
    this.tournamentPressed.emit(tournament);
  }

  onSharePress(tournament: CasualTournamentEntity) {
    this.share.emit(tournament);
  }

  trackBy(index: number, tournament: CasualTournamentEntity) {
    return tournament.uuid;
  }

  getPlayButtonText(tournament: CasualTournamentEntity) {
    const pass = this.getTournamentPass(tournament);

    if (pass.length > 0) {
      if (Number(pass[0].entries) >= 5000) {
        return 'UNLIMITED';
      }
      return `Pass ${pass[0].entries - pass[0].activeCount + 1}/${
        pass[0].entries
      }`;
    }

    return null;
  }

  passValue(tournament: CasualTournamentEntity): UserPasses | null {
    const pass = this.getTournamentPass(tournament);

    if (pass.length > 0) {
      return pass[0];
    }
    return null;
  }

  getTournamentPass(tournament: CasualTournamentEntity) {
    if (
      this.tournamentPassesForUser &&
      this.tournamentPassesForUser.length > 0
    ) {
      return this.tournamentPassesForUser.filter((pass) => {
        return (
          pass.eventPrototypeUuid === tournament.uuid &&
          pass.eventScheduleUuid === tournament.eventSchedules[0].uuid
        );
      });
    }
    return [];
  }

  floorFigure(figure: number): string {
    const decimals = 2;
    const d = Math.pow(10, decimals);
    return (parseInt(`${figure * d}`) / d).toFixed(decimals);
  }

  formatAmount(amount: number): string {
    if (amount) {
      const f = formatCurrency(
        parseFloat(this.floorFigure(amount)),
        this.locale,
        '',
        '',
        '1.2-2',
      );
      return f.toString();
    }

    return '0';
  }

  filterTournaments() {
    this.filteredTournaments = [];
    const sortedTournaments = [...this.tournaments];

    if (this.tournaments?.length > this.page.pageSize) {
      sortedTournaments.sort((a, b) =>
        a.lifeExperience < b.lifeExperience
          ? -1
          : a.lifeExperience > b.lifeExperience
            ? 1
            : 0,
      );
      for (let i = 0; i < sortedTournaments.length; i++) {
        if (
          i >= this.page.pageIndex * this.page.pageSize &&
          i < this.page.pageIndex * this.page.pageSize + this.page.pageSize
        ) {
          this.filteredTournaments.push(this.tournaments[i]);
        }
      }
    } else {
      this.filteredTournaments = sortedTournaments;
    }
  }

  onPageChange(event: IPageModel) {
    if (this.page.pageSize > event.pageSize) {
      this.scrollToTop();
    }

    this.page = {
      pageSize: event.pageSize,
      pageIndex: event.pageIndex,
    };

    this.loadMoreTournaments.emit(this.page);

    this.filterTournaments();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.configuration.pagination && changes['total']) {
      this.configuration.pagination.paginationTotal = this.total;
    }
    if (
      !changes.tournaments?.firstChange &&
      this.tournaments &&
      this.tournaments.length > 0 &&
      this.tournaments.length !== this.filteredTournaments.length
    ) {
      this.filterTournaments();
    }
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

function isTournamentEnded(tournament: CasualTournamentEntity): boolean {
  if (tournament.isComplete) {
    return true;
  }

  const date =
    typeof tournament.casualGameEndTime === 'string'
      ? new Date(tournament.casualGameEndTime)
      : tournament.casualGameEndTime;

  if (date) {
    return date.getTime() < Date.now();
  }

  return false;
}
